import type { UserNote } from "../@types/sharedSchema";

/**
 * Who a realtor's own note is shared with — the one rule for every notes surface (contact
 * notes tab, overview, seller notes, the property file).
 *
 * Since 2026-10-29 a new note starts SHARED with the author's office (an office manager asked
 * for his agents' notes to reach each other, and almost nobody ticked the share button: 5 of
 * 443 contact notes in his office). "Make private" is the opt-out.
 *
 * Shared means every office the author belongs to, and private means nobody. It used to be
 * "the office selected in the switcher", which left a realtor in two offices unable to make a
 * note private: unsharing removed only the selected office, the note still read as shared,
 * and the next click removed the same office again.
 *
 * Absent, never `[]`: the org listeners query `organizationIds array-contains`, and the
 * doc-level union is dropped when nothing is shared (see `computeDocOrgIds`).
 */
export const noteShareOrgIds = (organizations: ReadonlyArray<{ id?: string }>): string[] | undefined => {
  const ids = [...new Set(organizations.map((org) => org.id).filter((id): id is string => !!id))];
  return ids.length > 0 ? ids : undefined;
};

/** Sets a note's sharing: with every office the author is in, or with nobody. */
export const applyNoteShare = (note: UserNote, share: boolean, organizations: ReadonlyArray<{ id?: string }>): UserNote => {
  const { organizationIds: _drop, ...rest } = note;
  const orgIds = share ? noteShareOrgIds(organizations) : undefined;
  return orgIds ? { ...rest, organizationIds: orgIds } : (rest as UserNote);
};

/** A NEW note: shared with the author's offices by default. */
export const withDefaultNoteShare = (note: UserNote, organizations: ReadonlyArray<{ id?: string }>): UserNote =>
  applyNoteShare(note, true, organizations);
